import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { FormField } from "@/components/ui/form-field";
import { Switch } from "@/components/ui/switch";
import { DayField } from "../components/DayField";
import type { DayAxis } from "../models/day-axis";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, Euro, Plus, Trash, Ticket, Plane, FileText, Clock, Library, AlertTriangle, Search, Loader2 } from "lucide-react";
import { FlightActivity } from "../models/activity.model";
import { useFlightLookup } from "../hooks/useFlightLookup";
import { FlightPreviewCard } from "../components/FlightPreviewCard";
import { isTravelEmpty, resolvedFlightToTravelFields } from "../utils/flight-lookup.utils";
import { FlightLookupError, isValidFlightCode, normalizeFlightCode } from "@/services/flight.service";
import { getFlightCompanies } from "@/components/utils/flight-companies";
import { getAirports } from "@/components/utils/airports";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";
import { LibraryPicker } from "../components/LibraryPicker";
import type { LibraryActivity } from "@/lib/library/models/library-activity.model";

type FlightTab = 'details' | 'flights' | 'documents' | 'library' | 'lookup';

/** Tratta vuota: unico punto di verita' per stato iniziale, "aggiungi volo" e import da lookup. */
const createEmptyTravel = (): FlightActivity['travels'][0] => ({
  flightNumber: '',
  airline: '',
  link: '',
  from: '',
  to: '',
  fromDate: '',
  fromTime: '',
  toDate: '',
  toTime: ''
});

/** Valore del selettore "su quale tratta applico il volo trovato". */
const NEW_TRAVEL_TARGET = 'new';

/**
 * Riconoscimento volo sospeso: il frontend c'e' tutto, ma la edge function e la
 * tabella airlines non sono ancora in produzione. Finche' resta `false` il tab
 * si vede con il badge "presto", non si apre e nessuna ricerca parte.
 * Per riattivarlo basta rimettere `true`.
 */
const FLIGHT_LOOKUP_ENABLED = false;

interface FlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FlightActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FlightActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template ogni tratta si colloca su "Giorno N". */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
}

const FlightModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, readOnly = false }: FlightModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<FlightActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    address: '',
    travels: [
      {
        flightNumber: '',
        airline: '',
        link: '',
        from: '',
        to: '',
        fromDate: undefined,
        fromTime: '',
        toDate: undefined,
        toTime: ''
      }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({
        ...prev,
        travels: prev.travels?.map((t, i) => i === 0 ? { ...t, fromDate: defaultDate, toDate: defaultDate } : t) || prev.travels
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Import da libreria in due tempi: si sceglie, si conferma, poi si torna ai dati.
  const [pendingImport, setPendingImport] = useState<LibraryActivity | null>(null);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<FlightTab>('details');

  // Riconoscimento volo: il codice digitato e quello effettivamente cercato sono
  // due cose diverse — la ricerca parte dal bottone, non mentre si scrive.
  const [flightCode, setFlightCode] = useState('');
  const [searchedCode, setSearchedCode] = useState('');
  const [lookupTarget, setLookupTarget] = useState<string>(NEW_TRAVEL_TARGET);

  const lookupQuery = useFlightLookup(searchedCode, FLIGHT_LOOKUP_ENABLED && Boolean(searchedCode));

  const resetLookup = () => {
    setFlightCode('');
    setSearchedCode('');
  };

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
      setPendingImport(null);
      resetLookup();
      setLookupTarget(NEW_TRAVEL_TARGET);
    }
  }, [isOpen]);

  // Entrando nel tab: se c'e' una sola tratta ed e' ancora vuota, il volo trovato
  // va li' — chiedere "nuova tratta" su un form appena aperto sarebbe un passaggio
  // in piu' per il caso piu' comune.
  useEffect(() => {
    if (activeTab !== 'lookup') return;
    const travels = formData.travels || [];
    setLookupTarget(travels.length === 1 && isTravelEmpty(travels[0]) ? '0' : NEW_TRAVEL_TARGET);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Create options for AutocompleteSelect components
  const airlineOptions = getFlightCompanies().map(company => ({
    value: company,
    label: company
  }));

  const airportOptions = getAirports().map(airport => ({
    value: airport,
    label: airport
  }));

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['cost', 'description'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof FlightActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['airline', 'from', 'to', 'fromDate', 'fromTime', 'toDate', 'toTime'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });

        if (travel.from && travel.to && travel.from.trim() === travel.to.trim()) {
          newErrors[`travel_${index}_from`] = true;
          newErrors[`travel_${index}_to`] = true;
        }
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const getDateWithOffset = (dateString: string | null | undefined, offset: number) => {
    if (!dateString) return undefined;
    const baseDate = new Date(dateString);
    if (Number.isNaN(baseDate.getTime())) return undefined;
    baseDate.setDate(baseDate.getDate() + offset);
    return baseDate;
  };

  const fromMinDate = getDateWithOffset(itineraryStartDate, -1);
  const fromMaxDate = getDateWithOffset(itineraryEndDate, 1);
  const toMinDate = getDateWithOffset(itineraryStartDate, -1);
  const toMaxDate = getDateWithOffset(itineraryEndDate, 1);

  const addFlight = () => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []),
        createEmptyTravel()
      ]
    }));
  };

  const removeFlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateFlight = (index: number, field: keyof FlightActivity['travels'][0], value: string | number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((flight, i) => {
        if (i !== index) return flight;

        const updatedFlight = { ...flight, [field]: value };

        // se sto settando fromDate controllo che toDate sia dopo, altrimenti la resetto
        if (field === 'fromDate' && updatedFlight.toDate) {
          const fromDateValue = new Date(value);
          const toDateValue = new Date(updatedFlight.toDate);
          if (!Number.isNaN(fromDateValue.getTime()) && !Number.isNaN(toDateValue.getTime()) && toDateValue < fromDateValue) {
            updatedFlight.toDate = '';
          }
        }

        return updatedFlight;
      }) || []
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      const title = formData.travels[0].from + ' → ' + formData.travels[0].to;

      onSave({
        ...formData,
        title,
        type: 'flight' as const,
        date: formData.travels[0].fromDate
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    const detailsFailed = ['cost', 'description'].some(field => newErrors[field]);
    setActiveTab(detailsFailed ? 'details' : 'flights');
  };

  const requiredError = String(t('validation.fieldRequired'));

  // In modifica non si importa: il tab compare solo creando una nuova attività.
  const canImportFromLibrary = !initialData;

  const confirmImport = () => {
      if (!pendingImport) return;
      const imported = pendingImport.toItineraryActivity(defaultDate);
      // L'id resta quello di questa modale: l'import precompila un inserimento nuovo,
      // non porta con sé l'identità dell'attività di libreria.
      setFormData({ ...(imported as Partial<FlightActivity>), id: activityId });
      setPendingImport(null);
      setErrors({});
      setShowValidationBanner(false);
      setActiveTab('details');
  };
  const normalizedFlightCode = normalizeFlightCode(flightCode);
  const canSearchFlight = isValidFlightCode(flightCode);

  const runFlightLookup = () => {
    if (!canSearchFlight) return;
    // Rilanciando lo stesso codice React Query non rifarebbe nulla: se il risultato
    // e' ancora in cache va bene cosi', se era in errore si forza un nuovo tentativo.
    if (searchedCode === normalizedFlightCode) {
      lookupQuery.refetch();
      return;
    }
    setSearchedCode(normalizedFlightCode);
  };

  /**
   * Applica il volo trovato alla tratta scelta.
   * Sovrascrive solo i campi che il lookup conosce: link, note, costo e
   * "includi nel prezzo" restano quelli che il TD ha gia' messo.
   */
  const applyFlightLookup = () => {
    if (!lookupQuery.data) return;
    const fields = resolvedFlightToTravelFields(lookupQuery.data);

    setFormData(prev => {
      const travels = [...(prev.travels || [])];
      if (lookupTarget === NEW_TRAVEL_TARGET) {
        travels.push({ ...createEmptyTravel(), ...fields });
      } else {
        const index = Number(lookupTarget);
        if (Number.isNaN(index) || !travels[index]) return prev;
        travels[index] = { ...travels[index], ...fields };
      }
      return { ...prev, travels };
    });

    // I campi appena riempiti non possono piu' essere in errore.
    if (lookupTarget !== NEW_TRAVEL_TARGET) {
      setErrors(prev => {
        const next = { ...prev };
        Object.keys(next)
          .filter(key => key.startsWith(`travel_${lookupTarget}_`))
          .forEach(key => delete next[key]);
        return next;
      });
    }

    resetLookup();
    setActiveTab('flights');
  };

  const lookupErrorKey = lookupQuery.error instanceof FlightLookupError
    ? lookupQuery.error.i18nKey
    : 'modals.flightLookup.errors.unknown' as const;

  const hasDetailsError = ['cost', 'description'].some(field => errors[field]);
  // I voli hanno un tab loro: i loro errori non devono accendere "Dati".
  const hasFlightsError = Object.keys(errors).some(key => errors[key] && (key === 'travels' || key.startsWith('travel_')));

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.flight')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.flight')}
      formId="flight-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as FlightTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          icon: <Ticket className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasDetailsError,
          content: (
            <div className="space-y-5">
              {/* Prima riga: il volo non ha titolo ne indirizzo, resta il costo. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  id="cost"
                  label={t('modals.price')}
                  icon={<Euro className="h-3 w-3" />}
                  required
                  hint={t('modals.costHint')}
                  error={errors.cost ? requiredError : undefined}
                >
                  <Input
                    id="cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                    placeholder="0,00"
                    className="h-9 tabular-nums"
                    hasError={errors.cost}
                  />
                </FormField>

                <div className="flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-2.5">
                  <div className="space-y-0.5">
                    <p className="text-xs text-gray-700">{t('modals.includePrice')}</p>
                    <p className="text-[11px] text-gray-500">{t('modals.includePriceHint')}</p>
                  </div>
                  <Switch
                    checked={formData.include_price || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                  />
                </div>
              </div>


              <FormField
                label={t('modals.note')}
                required
                error={errors.description ? requiredError : undefined}
              >
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder={t('modals.shareClientPreferencesFlight')}
                  hasError={errors.description}
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'flights',
          label: t('modals.tabFlights'),
          icon: <Plane className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasFlightsError,
          content: (
            <div className="space-y-4">
              {/* Una tratta per blocco: il riquadro raggruppa i campi di un volo,
                  non un singolo campo. */}
                {formData.travels?.map((travel, index) => (
                  <div key={index} className="space-y-4 rounded-md border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">
                        {t('modals.flightN', { n: index + 1 })}
                      </span>
                      {formData.travels && formData.travels.length > 1 && index !== 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFlight(index)}
                          className="h-7 gap-1 px-2 text-xs font-normal text-gray-500"
                        >
                          <Trash className="h-3.5 w-3.5" /> {t('modals.deleteFlight')}
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label={t('modals.flightNumber')}
                        icon={<Ticket className="h-3 w-3" />}
                      >
                        <Input
                          value={travel.flightNumber}
                          onChange={(e) => updateFlight(index, 'flightNumber', e.target.value)}
                          placeholder="AZ 1234"
                          className="h-9"
                        />
                      </FormField>

                      <FormField
                        label={t('modals.airline')}
                        required
                        error={errors[`travel_${index}_airline`] ? requiredError : undefined}
                      >
                        <AutocompleteSelect
                          options={airlineOptions}
                          value={travel.airline}
                          onValueChange={(value) => updateFlight(index, 'airline', value)}
                          placeholder={t('modals.airline')}
                          searchPlaceholder={t('modals.searchAirlines')}
                          emptyMessage="No airlines found"
                          manual={true}
                          filterMode="includes"
                          hasError={errors[`travel_${index}_airline`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.link')}
                        icon={<Link className="h-3 w-3" />}
                        hint={t('modals.addSupportingLink')}
                      >
                        <Input
                          value={travel.link}
                          onChange={(e) => updateFlight(index, 'link', e.target.value)}
                          placeholder="https://..."
                          className="h-9"
                          hasError={errors[`travel_${index}_link`]}
                        />
                      </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label={t('modals.departureAirport')}
                        required
                        error={errors[`travel_${index}_from`] ? requiredError : undefined}
                      >
                        <AutocompleteSelect
                          options={airportOptions}
                          value={travel.from}
                          onValueChange={(value) => updateFlight(index, 'from', value)}
                          placeholder={t('modals.departureAirport')}
                          searchPlaceholder={t('modals.searchAirports')}
                          emptyMessage="No airports found"
                          manual={true}
                          filterMode="includes"
                          hasError={errors[`travel_${index}_from`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.departureDate')}
                        required
                        error={errors[`travel_${index}_fromDate`] ? requiredError : undefined}
                      >
                        <DayField
                          axis={axis}
                          date={travel.fromDate}
                          dayIndex={travel.fromDayIndex}
                          onDateChange={(date) => updateFlight(index, 'fromDate', date)}
                          onDayIndexChange={(dayIndex) => updateFlight(index, 'fromDayIndex', dayIndex)}
                          placeholder={String(t('modals.datePlaceholder'))}
                          hasError={errors[`travel_${index}_fromDate`]}
                          minDate={fromMinDate || undefined}
                          maxDate={fromMaxDate || undefined}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.time')}
                        icon={<Clock className="h-3 w-3" />}
                        required
                        error={errors[`travel_${index}_fromTime`] ? requiredError : undefined}
                      >
                        <Input
                          type="time"
                          value={travel.fromTime}
                          onChange={(e) => updateFlight(index, 'fromTime', e.target.value)}
                          className="h-9"
                          hasError={errors[`travel_${index}_fromTime`]}
                        />
                      </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField
                        label={t('modals.arrivalAirport')}
                        required
                        error={errors[`travel_${index}_to`] ? requiredError : undefined}
                      >
                        <AutocompleteSelect
                          options={airportOptions}
                          value={travel.to}
                          onValueChange={(value) => updateFlight(index, 'to', value)}
                          placeholder={t('modals.arrivalAirport')}
                          searchPlaceholder={t('modals.searchAirports')}
                          emptyMessage="No airports found"
                          manual={true}
                          filterMode="includes"
                          hasError={errors[`travel_${index}_to`]}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.arrivalDate')}
                        required
                        error={errors[`travel_${index}_toDate`] ? requiredError : undefined}
                      >
                        <DayField
                          axis={axis}
                          date={travel.toDate}
                          dayIndex={travel.toDayIndex}
                          onDateChange={(date) => updateFlight(index, 'toDate', date)}
                          onDayIndexChange={(dayIndex) => updateFlight(index, 'toDayIndex', dayIndex)}
                          placeholder={String(t('modals.datePlaceholder'))}
                          hasError={errors[`travel_${index}_toDate`]}
                          // L'arrivo non può precedere la partenza; può cadere il giorno dopo.
                          minDate={travel.fromDate || toMinDate || undefined}
                          maxDate={toMaxDate || undefined}
                          minIndex={travel.fromDayIndex || 1}
                          extraDays={1}
                        />
                      </FormField>

                      <FormField
                        label={t('modals.time')}
                        icon={<Clock className="h-3 w-3" />}
                        required
                        error={errors[`travel_${index}_toTime`] ? requiredError : undefined}
                      >
                        <Input
                          type="time"
                          value={travel.toTime}
                          onChange={(e) => updateFlight(index, 'toTime', e.target.value)}
                          className="h-9"
                          hasError={errors[`travel_${index}_toTime`]}
                        />
                      </FormField>
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFlight}
                  className="h-8 w-full border-dashed text-xs font-normal text-gray-600"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {t('modals.addFlight')}
                </Button>
              </div>
          ),
        },
        {
          key: 'documents',
          optional: true,
          label: t('modals.tabDocuments'),
          icon: <FileText className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.documentation?.length || 0) > 0,
          content: (
            <FormField label={t('modals.documentation')} hint={t('modals.documentationHint')}>
              <UploadMultiFiles
                files={formData.documentation || []}
                onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                label={t('modals.uploadBoardingPass')}
                maxFiles={5}
                itineraryId={itineraryId}
                activityId={activityId}
              />
            </FormField>
          ),
        },
        {
          key: 'lookup',
          group: 'import' as const,
          optional: true,
          comingSoon: !FLIGHT_LOOKUP_ENABLED,
          label: t('modals.flightLookup.tabLookup'),
          icon: <Search className="h-3.5 w-3.5 shrink-0" />,
          hasContent: FLIGHT_LOOKUP_ENABLED && Boolean(lookupQuery.data),
          content: (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">{t('modals.flightLookup.description')}</p>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <FormField
                    label={t('modals.flightLookup.codeLabel')}
                    icon={<Ticket className="h-3 w-3" />}
                    hint={t('modals.flightLookup.codeHint')}
                    hideOptional
                  >
                    <Input
                      value={flightCode}
                      onChange={(e) => setFlightCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        // Dentro il form dell'attivita' Invio salverebbe tutto: qui
                        // deve solo far partire la ricerca.
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          runFlightLookup();
                        }
                      }}
                      placeholder={t('modals.flightLookup.codePlaceholder')}
                      className="h-9 uppercase"
                      maxLength={8}
                    />
                  </FormField>
                </div>

                <Button
                  type="button"
                  onClick={runFlightLookup}
                  disabled={!canSearchFlight || lookupQuery.isFetching}
                  className="mt-[22px] h-9 shrink-0"
                >
                  {lookupQuery.isFetching
                    ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : <Search className="mr-1.5 h-3.5 w-3.5" />}
                  {t('modals.flightLookup.searchAction')}
                </Button>
              </div>

              {lookupQuery.isError && !lookupQuery.isFetching && (
                <p className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t(lookupErrorKey)}
                </p>
              )}

              {lookupQuery.data && !lookupQuery.isFetching && (
                <div className="space-y-3">
                  <FlightPreviewCard result={lookupQuery.data} />

                  {/* Le tratte sono piu' di una: va detto dove finisce il volo trovato. */}
                  <FormField label={t('modals.flightLookup.applyToLabel')} hideOptional>
                    <Select value={lookupTarget} onValueChange={setLookupTarget}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(formData.travels || []).map((_, index) => (
                          <SelectItem key={index} value={String(index)}>
                            {t('modals.flightN', { n: index + 1 })}
                          </SelectItem>
                        ))}
                        <SelectItem value={NEW_TRAVEL_TARGET}>
                          {t('modals.flightLookup.addAsNewTravel')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>

                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" onClick={applyFlightLookup}>
                      {t('modals.flightLookup.useFlightAction')}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={resetLookup}>
                      {t('common.cancel')}
                    </Button>
                  </div>

                  <p className="text-[11px] text-gray-500">{t('modals.flightLookup.overwriteHint')}</p>
                </div>
              )}
            </div>
          ),
        },
        {
          key: 'library',
          group: 'import' as const,
          optional: true,
          hidden: !canImportFromLibrary,
          label: t('library.tabImport'),
          icon: <Library className="h-3.5 w-3.5 shrink-0" />,
          content: (
            <div className="space-y-4">
              {pendingImport && (
                <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t('library.importConfirmTitle', { title: pendingImport.title })}
                  </p>
                  <p className="text-[11px] text-amber-700">{t('library.importConfirmBody')}</p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={confirmImport}>
                      {t('library.importConfirmAction')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingImport(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <LibraryPicker
                activityType="flight"
                enabled={isOpen && activeTab === 'library'}
                onSelect={setPendingImport}
                listClassName="max-h-[40vh]"
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default FlightModal;
